import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap, catchError, map, startWith } from 'rxjs/operators';
import { ApprovalWorkflowService, ApprovalRequest, WorkflowStats, ProjectStage, StageTransition } from '../shared/services/approval-workflow.service';
import { ApiService } from '../shared/services/api.service';
import { ToastrMsgService } from '../shared/services/shared-services/toastr.service';

interface Project {
  id: number;
  project_name: string;
  project_identifier: string;
  company_name: string;
  client_name: string;
  stage: string;
  status: string;
  domain: string;
  infrastructure_type: string;
}

@Component({
  selector: 'app-approval-workflow',
  templateUrl: './approval-workflow.component.html',
  styleUrls: ['./approval-workflow.component.css']
})
export class ApprovalWorkflowComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data properties
  approvalRequests: ApprovalRequest[] = [];
  workflowStats: WorkflowStats | null = null;
  projectStages: any[] = [{id: 1, name: 'Pre-Planning'}, {id: 2, name: 'Planning'}, {id: 3, name: 'Survey'}, {id: 4, name: 'Design Revisions & Scope Adjustments'}, {id: 5, name: 'Execution'}, {id: 6, name: 'Closure'}];
  selectedRequest: ApprovalRequest | null = null;
  
  // Loading states
  loading = false;
  loadingStats = false;
  loadingRequests = false;
  
  // Pagination
  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  itemsPerPage = 10;
  
  // Filters
  statusFilter = '';
  searchTerm = '';
  
  // Forms
  approvalForm: FormGroup;
  requestForm: FormGroup;
  
  // Dialog states
  showApprovalDialog = false;
  showRequestDialog = false;
  showHistoryDialog = false;
  
  // Current user
  currentUserId: number;
  searchKeyword = "";
  allprojects: Project[] = [];
  
  // Project search properties
  filteredProjects: Project[] = [];
  projectSearchTerm = '';
  isProjectSearching = false;
  projectSearchSubject = new Subject<string>();
  constructor(
    private approvalService: ApprovalWorkflowService,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private apiService: ApiService,
    private toastrMsgService: ToastrMsgService,
  ) {
    this.currentUserId = this.getCurrentUserId();
    this.initializeForms();
  }

  ngOnInit(): void {
    this.loadWorkflowStats();
    this.loadApprovalRequests();
    // this.loadProjectStages();
    this.setupProjectSearch();
    this.loadInitialProjects();
    this.requestForm.get('projectId')!.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || ''))
    ).subscribe(filtered => {
      this.filteredProjects = filtered;
    });
  }

  private _filter(value: string): any {
    const filterValue = value.toLowerCase();
    return this.allprojects.filter((project:any) =>
      project.project_name.toLowerCase().includes(filterValue)
    );
  }

  onProjectSelected(event: any) {
    const selectedProject = this.allprojects.find(
      (c:any) => c.project_name === event.option.value
    );
    if (selectedProject && selectedProject.id) {
      this.requestForm.patchValue({
        projectId: selectedProject.id
      });
    }  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForms(): void {
    this.approvalForm = this.formBuilder.group({
      action: ['approve', Validators.required],
      notes: ['', Validators.required]
    });

    this.requestForm = this.formBuilder.group({
      projectId: ['', Validators.required],
      toStageId: ['', Validators.required],
      requestReason: ['', Validators.required]
    });
  }

  private getCurrentUserId(): number {
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.user.id;
    }
    return 0;
  }

  // Load workflow statistics
  loadWorkflowStats(): void {
    this.loadingStats = true;
    this.approvalService.getWorkflowStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stats) => {
          this.workflowStats = stats;
          this.loadingStats = false;
        },
        error: (error) => {
          console.error('Error loading workflow stats:', error);
          this.loadingStats = false;
          this.showNotification('Failed to load workflow statistics', 'error');
        }
      });
  }

  // Load approval requests
  loadApprovalRequests(): void {
    this.loadingRequests = true;
    const params: any = {
      page: this.currentPage,
      limit: this.itemsPerPage
    };

    if (this.statusFilter) {
      params.status = this.statusFilter;
    }

    this.approvalService.getApprovalRequests(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.approvalRequests = response.data;
          this.currentPage = response.pagination.current_page;
          this.totalPages = response.pagination.total_pages;
          this.totalItems = response.pagination.total_items;
          this.itemsPerPage = response.pagination.items_per_page;
          this.loadingRequests = false;
        },
        error: (error) => {
          console.error('Error loading approval requests:', error);
          this.loadingRequests = false;
          this.showNotification('Failed to load approval requests', 'error');
        }
      });
  }

  // Load project stages
  loadProjectStages(): void {
    this.approvalService.getProjectStages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stages) => {
          this.projectStages = stages;
        },
        error: (error) => {
          console.error('Error loading project stages:', error);
        }
      });
  }

  // Setup project search with debouncing
  setupProjectSearch(): void {
    this.projectSearchSubject
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300), // Wait 300ms after user stops typing
        distinctUntilChanged(), // Only emit if value has changed
        switchMap(searchTerm => this.searchProjects(searchTerm))
      )
      .subscribe({
        next: (projects) => {
          this.filteredProjects = projects;
          this.isProjectSearching = false;
        },
        error: (error) => {
          console.error('Error searching projects:', error);
          this.isProjectSearching = false;
          this.toastrMsgService.error('Failed to search projects');
        }
      });
  }

  // Load initial projects for dropdown
  loadInitialProjects(): void {
    this.isProjectSearching = true;
    this.apiService.getProjectListForDropdown({ limit: 50 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.allprojects = response.data || [];
          this.filteredProjects = [...this.allprojects];
          this.isProjectSearching = false;
        },
        error: (error) => {
          console.error('Error loading initial projects:', error);
          this.isProjectSearching = false;
          this.toastrMsgService.error('Failed to load projects');
        }
      });
  }

  // Search projects with optimized API
  searchProjects(searchTerm: string): Observable<Project[]> {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return of(this.allprojects);
    }

    this.isProjectSearching = true;
    return this.apiService.searchProjects({ 
      search: searchTerm.trim(), 
      limit: 20,
      status: 'active'
    }).pipe(
      map((response: any) => response.data || []),
      catchError(error => {
        console.error('Error in project search:', error);
        this.toastrMsgService.error('Failed to search projects');
        return of([]);
      })
    );
  }

  // Handle project search input
  onProjectSearchChange(searchTerm: string): void {
    this.projectSearchTerm = searchTerm;
    this.projectSearchSubject.next(searchTerm);
  }

  // Filter projects for display
  getDisplayProjects(): Project[] {
    if (this.projectSearchTerm && this.projectSearchTerm.trim().length > 0) {
      return this.filteredProjects;
    }
    return this.allprojects;
  }

  // Get project display text
  getProjectDisplayText(project: Project): string {
    if (!project) return '';
    return `${project.project_name} (${project.company_name}) - ${project.client_name}`;
  }

  // Handle project selection from autocomplete
  onProjectSelectedOld(event: any): void {
    const selectedProject = event.option.value;
    if (selectedProject && selectedProject.id) {
      this.requestForm.patchValue({
        projectId: selectedProject.id
      });
    }
  }

  // Load single approval request
  loadApprovalRequest(id: number): void {
    this.approvalService.getApprovalRequest(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (request) => {
          this.selectedRequest = request;
        },
        error: (error) => {
          console.error('Error loading approval request:', error);
          this.showNotification('Failed to load approval request details', 'error');
        }
      });
  }

  // Open approval dialog
  openApprovalDialog(request: ApprovalRequest): void {
    this.selectedRequest = request;
    this.approvalForm.reset({
      action: 'approve',
      notes: ''
    });
    this.showApprovalDialog = true;
  }

  // Process approval
  processApproval(): void {
    if (this.approvalForm.valid && this.selectedRequest) {
      const formData = this.approvalForm.value;
      
      this.approvalService.processApproval(this.selectedRequest.id, {
        action: formData.action,
        notes: formData.notes
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showApprovalDialog = false;
          this.showNotification(`Request ${formData.action}d successfully`, 'success');
          this.loadApprovalRequests();
          this.loadWorkflowStats();
        },
        error: (error) => {
          console.error('Error processing approval:', error);
          this.showNotification('Failed to process approval', 'error');
        }
      });
    }
  }

  // Cancel approval request
  cancelApprovalRequest(request: ApprovalRequest): void {
    if (confirm('Are you sure you want to cancel this approval request?')) {
      this.approvalService.cancelApprovalRequest(request.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.showNotification('Approval request cancelled successfully', 'success');
            this.loadApprovalRequests();
            this.loadWorkflowStats();
          },
          error: (error) => {
            console.error('Error cancelling approval request:', error);
            this.showNotification('Failed to cancel approval request', 'error');
          }
        });
    }
  }

  // Create new approval request
  createApprovalRequest(): void {
    if (this.requestForm.valid) {
      const formData = this.requestForm.value;
      
      this.approvalService.createApprovalRequest({
        projectId: formData.projectId,
        toStageId: formData.toStageId,
        requestReason: formData.requestReason
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showRequestDialog = false;
          this.requestForm.reset();
          this.showNotification('Approval request created successfully', 'success');
          this.loadApprovalRequests();
          this.loadWorkflowStats();
        },
        error: (error) => {
          console.error('Error creating approval request:', error);
          this.showNotification('Failed to create approval request', 'error');
        }
      });
    }
  }

  // Open request dialog
  openRequestDialog(): void {
    this.requestForm.reset();
    this.showRequestDialog = true;
  }

  // Open history dialog
  openHistoryDialog(request: ApprovalRequest): void {
    this.selectedRequest = request;
    this.showHistoryDialog = true;
  }

  // Pagination
  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadApprovalRequests();
  }

  // Filter changes
  onStatusFilterChange(): void {
    this.currentPage = 1;
    this.loadApprovalRequests();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadApprovalRequests();
  }

  // Utility methods
  canApprove(request: ApprovalRequest): boolean {
    return this.approvalService.canApprove(request, this.currentUserId);
  }

  canCancel(request: ApprovalRequest): boolean {
    return this.approvalService.canCancel(request, this.currentUserId);
  }

  getStatusClass(status: string): string {
    return this.approvalService.getStatusClass(status);
  }

  getStatusIcon(status: string): string {
    return this.approvalService.getStatusIcon(status);
  }

  getStatusText(status: string): string {
    return this.approvalService.getStatusText(status);
  }

  formatDate(dateString: string): string {
    return this.approvalService.formatDate(dateString);
  }

  // Action methods for history timeline
  getActionIcon(action: string): string {
    switch (action) {
      case 'requested':
        return 'add_circle';
      case 'approved':
        return 'check_circle';
      case 'rejected':
        return 'cancel';
      case 'cancelled':
        return 'block';
      default:
        return 'help';
    }
  }

  getActionText(action: string): string {
    switch (action) {
      case 'requested':
        return 'Request Submitted';
      case 'approved':
        return 'Request Approved';
      case 'rejected':
        return 'Request Rejected';
      case 'cancelled':
        return 'Request Cancelled';
      default:
        return 'Unknown Action';
    }
  }

  // Notifications
  private showNotification(message: string, type: 'success' | 'error'): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: type === 'success' ? ['success-snackbar'] : ['error-snackbar']
    });
  }

  // Close dialogs
  closeApprovalDialog(): void {
    this.showApprovalDialog = false;
    this.selectedRequest = null;
  }

  closeRequestDialog(): void {
    this.showRequestDialog = false;
  }

  closeHistoryDialog(): void {
    this.showHistoryDialog = false;
    this.selectedRequest = null;
  }


  // Legacy method - replaced by optimized search
  fetchData() {
    // This method is deprecated - use loadInitialProjects() instead
    this.loadInitialProjects();
  }
}
